import { useState } from "react";
import { api, errText } from "@/lib/api";
import { PROSPECT_STATUSES, PARTNER_STATUSES } from "@/lib/partners";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] uppercase tracking-[0.14em] text-gray-500">{label}</Label>
    {children}
  </div>
);

const FIELDS = [
  ["first_name", "First Name"], ["last_name", "Last Name"], ["display_name", "Display Name"],
  ["brokerage", "Brokerage"], ["team_name", "Team / Realtor Name"], ["city", "City"],
  ["territory", "Primary Territory"], ["email", "Email"], ["phone", "Phone"], ["website", "Website"],
  ["instagram", "Instagram"], ["linkedin", "LinkedIn"], ["headshot_url", "Headshot URL"],
  ["brokerage_logo_url", "Brokerage Logo URL"], ["realtor_logo_url", "Realtor Logo URL"], ["slug", "Custom URL Slug"],
];

const NUM_FIELDS = [
  ["estimated_transactions", "Annual Transactions"], ["estimated_volume", "Annual Sales Volume ($)"],
  ["average_home_price", "Average Home Price ($)"], ["years_active", "Years in Business"],
  ["prospect_score", "ARK Prospect Score (0-100)"],
];

export const RealtorForm = ({ realtor, onSaved }) => {
  const [form, setForm] = useState(() => {
    const f = {};
    FIELDS.forEach(([k]) => (f[k] = realtor[k] || ""));
    NUM_FIELDS.forEach(([k]) => (f[k] = realtor[k] ?? 0));
    f.notes = realtor.notes || "";
    f.prospect_status = realtor.prospect_status;
    f.partner_status = realtor.partner_status;
    f.campaign_id = realtor.campaign_id || null;
    return f;
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const num = (v) => Number(String(v).replace(/[^0-9.]/g, "")) || 0;
      const payload = { ...form };
      NUM_FIELDS.forEach(([k]) => (payload[k] = num(form[k])));
      const { data } = await api.put(`/realtors/${realtor.id}`, payload);
      toast.success("Realtor saved");
      onSaved?.(data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="realtor-edit-form">
      <div className="bg-white border border-gray-200 p-6 sm:p-8 grid sm:grid-cols-2 gap-5">
        {FIELDS.map(([k, label]) => (
          <F key={k} label={label}>
            <Input data-testid={`edit-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} className="rounded-sm h-11" />
          </F>
        ))}
        {NUM_FIELDS.map(([k, label]) => (
          <F key={k} label={label}>
            <Input data-testid={`edit-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} className="rounded-sm h-11" />
          </F>
        ))}
        <F label="Prospect Status">
          <Select value={form.prospect_status} onValueChange={(v) => set("prospect_status", v)}>
            <SelectTrigger data-testid="edit-prospect-status" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
            <SelectContent>{PROSPECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <F label="Partner Status">
          <Select value={form.partner_status} onValueChange={(v) => set("partner_status", v)}>
            <SelectTrigger data-testid="edit-partner-status" className="rounded-sm h-11"><SelectValue /></SelectTrigger>
            <SelectContent>{PARTNER_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        <div className="sm:col-span-2">
          <F label="Notes">
            <Textarea data-testid="edit-notes" rows={4} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="rounded-sm" />
          </F>
        </div>
      </div>
      <Button
        data-testid="save-realtor-edit-btn"
        onClick={save}
        disabled={saving}
        className="h-12 px-10 bg-black hover:bg-black/85 text-white rounded-sm tracking-[0.15em] uppercase text-xs btn-lift"
      >
        {saving ? "Saving..." : "Save Realtor"}
      </Button>
    </div>
  );
};
